import { ErrorDto } from "../types/PortalError";
import { getCurrentESTDate } from "./dateHelper";
import { ERRORMESSAGES, USERINFO } from "../data/constants";
import { appInsights } from "./AppInsights";
import { addPortalError } from "../services/errorService";
import { getIpAddress } from "../services/userService";
import { getModuleName } from "./commonHelper";
import { getUserId } from "../services/stores/userStore";

// Tracks errors already being handled by the React ErrorBoundary so
// window.onunhandledrejection and window.onerror (which React 18 dev-mode
// fires for boundary catches) do not also navigate to /error-page or show
// the CRA dev overlay.
const boundaryHandledErrors = new WeakSet<Error>();

export const markAsBoundaryHandled = (error: Error): void => {
  boundaryHandledErrors.add(error);
};

export const isBoundaryHandled = (error: unknown): boolean =>
  error instanceof Error && boundaryHandledErrors.has(error);

/**
 * Logs a frontend error directly to Portal_ErrorLog via POST /portal/error.
 * Calls addPortalError directly (not via executeApi) so it works regardless
 * of React component mount state.  getIpAddress failures are non-fatal.
 */
export async function logPortalErrorGlobal({
  errorSummary,
  errorDetails,
  userId,
}: {
  errorSummary: string;
  errorDetails: string;
  // Nullable: no user is signed in yet on /signin, where the `userId ?? 3`
  // fallback below supplies the system user Portal_ErrorLog.UserId requires.
  userId: number | null;
}) {
  if (!errorSummary) return;

  try {
    const ipAddress = await getIpAddress().catch(() => "");
    const errorData: ErrorDto = {
      userId: userId ?? 3,
      errorDate: getCurrentESTDate(),
      moduleID: null,
      moduleName: getModuleName(),
      summary: errorSummary,
      details: errorDetails,
      dataObjectJson: "",
      ipAddress,
      userAgent: navigator.userAgent,
      deviceSize: window.innerWidth.toString(),
      sessionID: localStorage.getItem(USERINFO.SESSIONVALUE),
    };

    await addPortalError(errorData);
  } catch (apiError) {
    appInsights?.trackException({
      exception: new Error(ERRORMESSAGES.APIErrorLogging, apiError),
    });
  }
}

/**
 * Logs an error the app has already handled and surfaced to the user (a toast),
 * so it still reaches Portal_ErrorLog.  Handled errors never reach ErrorBoundary
 * or window.onunhandledrejection, which are the only other paths that log.
 *
 * The user id comes from the persisted user store, not UserContext, so this also
 * works on /signin where no UserProvider value has been set yet.  Deliberately
 * not awaited by callers — the toast must not wait on the log round-trip.
 */
export function logHandledError(errorSummary: string, errorDetails = ""): void {
  void logPortalErrorGlobal({
    errorSummary,
    errorDetails,
    userId: getUserId(),
  });
}
